import { cn } from 'utils/styles';
import { niceStep } from 'components/Widget/shared/axis';
import { formatCompactCurrency } from 'components/Widget/shared/currency';
import { CHART_COPY, SEGMENT_FILLS, STACK_ORDER } from './constants';
import type { ApySavingsResult, ApySavingsSnapshot, ApySavingsStackSegment } from './types';

const TICK_COUNT = 4;

const PLOT_HEIGHT = 'h-48';

/** Top-down, so a segment's `border-t` separator always lands on an interior boundary. */
const TOP_DOWN_KEYS = [...STACK_ORDER].reverse();

/** The last segment takes what is left of 100 rather than its own quotient, so the stack has no seam. */
const stackSegments = (bucket: ApySavingsSnapshot): ApySavingsStackSegment[] => {
  const keys = TOP_DOWN_KEYS.filter(key => bucket[key] > 0);
  let used = 0;

  return keys.map((key, index) => {
    const basisPct = index === keys.length - 1 ? 100 - used : (bucket[key] / bucket.totalSavings) * 100;

    used += basisPct;

    return { key, basisPct };
  });
};

interface ApySavingsBarGraphProps {
  result: ApySavingsResult;
}

export const ApySavingsBarGraph = ({ result }: ApySavingsBarGraphProps) => {
  const tallestBar = Math.max(...result.buckets.map(bucket => bucket.totalSavings));
  const step = niceStep(tallestBar / TICK_COUNT);
  const yMax = step * TICK_COUNT;
  // Decides months-vs-years for the whole axis, so one bucket cannot label itself out of step.
  const longestBucketMonths = Math.max(...result.buckets.map(bucket => bucket.elapsedMonths));

  return (
    <div className="relative flex gap-2 pl-12">
      {/* Scaffolding only: every figure the axis marks is also in a bar's name and the breakdown below. */}
      <div aria-hidden className={cn('absolute inset-x-0 top-0', PLOT_HEIGHT)}>
        {Array.from({ length: TICK_COUNT + 1 }, (_, index) => (
          <div
            key={index}
            style={{ bottom: `${(index / TICK_COUNT) * 100}%` }}
            className="absolute inset-x-0 flex translate-y-1/2 items-center gap-2">
            <span className="w-10 shrink-0 text-right text-legal text-content-secondary">
              {formatCompactCurrency(step * index)}
            </span>
            <span className="h-px flex-1 bg-border-primary" />
          </div>
        ))}
      </div>

      {result.buckets.map(bucket => (
        <div key={bucket.elapsedMonths} className="relative flex min-w-3 flex-1 flex-col">
          <div className={cn('flex items-end', PLOT_HEIGHT)}>
            {/* Scaled against the shared ceiling; the segments inside divide the bar's own height. */}
            <ul
              aria-label={CHART_COPY.barLabel(bucket, longestBucketMonths)}
              style={{ height: `${(bucket.totalSavings / yMax) * 100}%` }}
              className="flex w-full flex-col overflow-hidden rounded-md border border-border-primary">
              {stackSegments(bucket).map((segment, index) => (
                <li
                  key={segment.key}
                  style={{ flexBasis: `${segment.basisPct}%` }}
                  // The floor clears 3px so the separator cannot leave a segment with no fill at all.
                  className={cn('min-h-[3px]', SEGMENT_FILLS[segment.key], index > 0 && 'border-t border-bg-primary')}
                />
              ))}
            </ul>
          </div>
          <p className="mt-2 text-center text-legal text-content-secondary">
            {CHART_COPY.bucketLabel(bucket.elapsedMonths, longestBucketMonths)}
          </p>
        </div>
      ))}
    </div>
  );
};
